import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import NotificationBell from "./NotificationBell";

const CATEGORIES = [
  { key: "all",      label: "전체글" },
  { key: "notice",   label: "공지" },
  { key: "자유글",   label: "자유글" },
  { key: "문의",     label: "문의" },
  { key: "mine",     label: "내가 쓴 글" },
];

const PAGE_SIZE = 10;

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "방금";
  if (m < 60) return `${m}분 전`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}시간 전`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}일 전`;
  return new Date(dateStr).toLocaleDateString("ko-KR");
}

export default function Board({ onHome, initialTab }) {
  const { user } = useAuth();

  // 목록 상태
  const [tab, setTab]           = useState(initialTab || "all");
  const [posts, setPosts]       = useState([]);
  const [page, setPage]         = useState(0);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // 글 작성
  const [showWrite, setShowWrite]       = useState(false);
  const [writeCategory, setWriteCategory] = useState("자유글");
  const [writeTitle, setWriteTitle]     = useState("");
  const [writeContent, setWriteContent] = useState("");
  const [writeSaving, setWriteSaving]   = useState(false);

  // 글 수정
  const [editingPost, setEditingPost]   = useState(null);

  // 상세보기
  const [detail, setDetail]             = useState(null);
  const [comments, setComments]         = useState([]);
  const [commentText, setCommentText]   = useState("");
  const [commentSaving, setCommentSaving] = useState(false);

  // 공지사항
  const [notices, setNotices]           = useState([]);
  const [openNoticeId, setOpenNoticeId] = useState(null);
  const [isAdmin, setIsAdmin]           = useState(false);
  const [noticeFormMode, setNoticeFormMode] = useState(null);
  const [editingNotice, setEditingNotice]   = useState(null);
  const [noticeTitle, setNoticeTitle]       = useState("");
  const [noticeContent, setNoticeContent]   = useState("");
  const [noticeSaving, setNoticeSaving]     = useState(false);

  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const containerRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    if (containerRef.current && containerRef.current.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY;
    } else {
      touchStartY.current = 0;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!touchStartY.current) return;
    const diff = e.touches[0].clientY - touchStartY.current;
    if (diff > 0) setPullY(Math.min(diff * 0.4, 80));
  }, []);

  const handleTouchEnd = useCallback(async () => {
    if (pullY > 50) {
      setRefreshing(true);
      setRefreshKey((k) => k + 1);
      setRefreshing(false);
    }
    setPullY(0);
    touchStartY.current = 0;
  }, [pullY]);

  // 어드민 체크
  useEffect(() => {
    if (!user) return;
    supabase.from("admins").select("id").eq("user_id", user.id).maybeSingle()
      .then(({ data }) => setIsAdmin(!!data));
  }, [user]);

  // 공지 조회
  const fetchNotices = useCallback(async () => {
    const { data } = await supabase
      .from("notices")
      .select("*")
      .order("created_at", { ascending: false });
    setNotices(data || []);
  }, []);

  useEffect(() => { if (tab === "notice" || tab === "all") fetchNotices(); }, [tab, fetchNotices, refreshKey]);

  const openNoticeForm = (mode, notice = null) => {
    setNoticeFormMode(mode);
    setEditingNotice(notice);
    setNoticeTitle(notice?.title || "");
    setNoticeContent(notice?.content || "");
  };
  const closeNoticeForm = () => { setNoticeFormMode(null); setEditingNotice(null); setNoticeTitle(""); setNoticeContent(""); };

  const saveNotice = async () => {
    if (!noticeTitle.trim() || !noticeContent.trim()) return;
    setNoticeSaving(true);
    if (noticeFormMode === "edit" && editingNotice) {
      await supabase.from("notices").update({ title: noticeTitle.trim(), content: noticeContent.trim() }).eq("id", editingNotice.id);
    } else {
      await supabase.from("notices").insert([{ title: noticeTitle.trim(), content: noticeContent.trim() }]);
    }
    setNoticeSaving(false);
    closeNoticeForm();
    setRefreshKey((k) => k + 1);
  };

  const deleteNotice = async (id) => {
    if (!window.confirm("이 공지를 삭제하시겠어요?")) return;
    await supabase.from("notices").delete().eq("id", id);
    setRefreshKey((k) => k + 1);
  };

  const fetchPosts = useCallback(async () => {
    if (tab === "notice") { setLoading(false); setPosts([]); return; }
    setLoading(true);
    let query = supabase
      .from("board_posts")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

    if (tab === "문의") query = query.eq("category", "문의");
    else if (tab === "자유글") query = query.eq("category", "자유글");
    else if (tab === "mine" && user) query = query.eq("user_id", user.id);

    const { data, count } = await query;
    setPosts(data || []);
    setTotal(count || 0);
    setLoading(false);
  }, [tab, page, user, refreshKey]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleTabChange = (key) => {
    setTab(key);
    setPage(0);
    setDetail(null);
    setShowWrite(false);
    setEditingPost(null);
  };

  // 글 작성/수정 제출
  const handleSubmitPost = async () => {
    if (!writeTitle.trim() || !writeContent.trim()) return;
    setWriteSaving(true);

    let err = null;
    if (editingPost) {
      const { error } = await supabase.from("board_posts").update({
        title: writeTitle.trim(),
        content: writeContent.trim(),
        category: writeCategory,
      }).eq("id", editingPost.id);
      err = error;
    } else {
      const { error } = await supabase.from("board_posts").insert([{
        user_id: user.id,
        email: user.email,
        nickname: user.email ? user.email.split("@")[0] : "익명",
        title: writeTitle.trim(),
        content: writeContent.trim(),
        category: writeCategory,
      }]);
      err = error;
    }

    setWriteSaving(false);

    if (err) {
      alert("저장에 실패했어요: " + err.message);
      return;
    }

    setShowWrite(false);
    setEditingPost(null);
    setWriteTitle("");
    setWriteContent("");
    setWriteCategory("자유글");
    setPage(0);
    setTab("all");
    setRefreshKey((k) => k + 1);
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setWriteTitle(post.title);
    setWriteContent(post.content);
    setWriteCategory(post.category);
    setShowWrite(true);
    setDetail(null);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("글을 삭제하시겠어요?")) return;
    await supabase.from("board_comments").delete().eq("post_id", postId);
    const { error } = await supabase.from("board_posts").delete().eq("id", postId);
    if (error) {
      alert("삭제 실패: " + error.message);
      return;
    }
    setDetail(null);
    setRefreshKey((k) => k + 1);
  };

  // 상세보기
  const openDetail = async (post) => {
    setDetail(post);
    setShowWrite(false);
    const { data } = await supabase
      .from("board_comments")
      .select("*")
      .eq("post_id", post.id)
      .order("created_at", { ascending: true });
    setComments(data || []);
    setCommentText("");
  };

  const submitComment = async () => {
    if (!commentText.trim() || !user) return;
    setCommentSaving(true);
    await supabase.from("board_comments").insert([{
      post_id: detail.id,
      user_id: user.id,
      email: user.email,
      nickname: user.email ? user.email.split("@")[0] : "익명",
      content: commentText.trim(),
    }]);
    setCommentSaving(false);
    setCommentText("");
    const { data } = await supabase
      .from("board_comments")
      .select("*")
      .eq("post_id", detail.id)
      .order("created_at", { ascending: true });
    setComments(data || []);
  };

  const deleteComment = async (id) => {
    if (!window.confirm("댓글을 삭제하시겠어요?")) return;
    await supabase.from("board_comments").delete().eq("id", id);
    setComments((prev) => prev.filter((c) => c.id !== id));
  };

  // ─── 글 상세보기 ───
  if (detail) {
    const isAuthor = user?.id === detail.user_id;
    return (
      <div style={s.container}>
        <div style={s.header}>
          <button style={s.backBtn} onClick={() => setDetail(null)}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="#E24B4A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            목록으로 돌아가기
          </button>
          <span style={s.headerTitle}>게시판</span>
          {isAuthor ? (
            <div style={{ display: "flex", gap: 6 }}>
              <button style={s.headerActionBtn} onClick={() => handleEdit(detail)}>수정</button>
              <button style={{ ...s.headerActionBtn, color: "#E24B4A", borderColor: "#fecaca" }} onClick={() => handleDelete(detail.id)}>삭제</button>
            </div>
          ) : (
            <div style={{ width: 64 }} />
          )}
        </div>

        <div style={s.body}>
          <div style={s.detailCard}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <span style={s.catBadge}>{detail.category}</span>
              <span style={{ fontSize: 11, color: "#9ca3af" }}>{timeAgo(detail.created_at)}</span>
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#111827", marginBottom: 8 }}>{detail.title}</div>
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 14 }}>{detail.nickname || detail.email}</div>
            <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{detail.content}</div>
          </div>

          {/* 댓글 */}
          <div style={{ ...s.sectionTitle, marginTop: 20 }}>댓글 {comments.length}개</div>
          {comments.length === 0 ? (
            <div style={{ textAlign: "center", padding: "20px", fontSize: 13, color: "#9ca3af" }}>아직 댓글이 없어요</div>
          ) : (
            comments.map((c) => (
              <div key={c.id} style={s.commentItem}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{c.nickname || c.email}</span>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 11, color: "#9ca3af" }}>{timeAgo(c.created_at)}</span>
                    {user?.id === c.user_id && (
                      <button style={s.commentDeleteBtn} onClick={() => deleteComment(c.id)}>삭제</button>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.6 }}>{c.content}</div>
              </div>
            ))
          )}

          {/* 댓글 작성 */}
          {user ? (
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <input
                style={{ ...s.input, flex: 1 }}
                placeholder="댓글을 입력하세요"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitComment()}
                maxLength={500}
              />
              <button
                style={{ ...s.submitSmallBtn, opacity: commentSaving ? 0.6 : 1 }}
                onClick={submitComment}
                disabled={commentSaving || !commentText.trim()}
              >
                등록
              </button>
            </div>
          ) : (
            <div style={{ textAlign: "center", fontSize: 13, color: "#9ca3af", marginTop: 12 }}>로그인 후 댓글을 작성할 수 있어요</div>
          )}
        </div>
      </div>
    );
  }

  // ─── 글 작성/수정 폼 ───
  if (showWrite) {
    return (
      <div style={s.container}>
        <div style={s.header}>
          <button style={s.backBtn} onClick={() => { setShowWrite(false); setEditingPost(null); setWriteTitle(""); setWriteContent(""); setWriteCategory("자유글"); }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="#E24B4A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            취소
          </button>
          <span style={s.headerTitle}>{editingPost ? "글 수정" : "글 작성"}</span>
          <div style={{ width: 64 }} />
        </div>

        <div style={s.body}>
          <div style={{ marginBottom: 16 }}>
            <div style={s.sectionTitle}>카테고리</div>
            <div style={{ display: "flex", gap: 8 }}>
              {[{ key: "자유글", label: "자유글" }, { key: "문의", label: "문의" }].map((c) => (
                <button
                  key={c.key}
                  style={{ ...s.catChip, background: writeCategory === c.key ? "#E24B4A" : "#fff", color: writeCategory === c.key ? "#fff" : "#6b7280", borderColor: writeCategory === c.key ? "#E24B4A" : "#e5e7eb" }}
                  onClick={() => setWriteCategory(c.key)}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={s.sectionTitle}>제목</div>
            <input
              style={s.input}
              placeholder="제목을 입력하세요"
              value={writeTitle}
              onChange={(e) => setWriteTitle(e.target.value)}
              maxLength={100}
            />
          </div>
          <div style={{ marginBottom: 20 }}>
            <div style={s.sectionTitle}>내용</div>
            <textarea
              style={{ ...s.input, resize: "none", height: 160, lineHeight: 1.7 }}
              placeholder="내용을 입력하세요"
              value={writeContent}
              onChange={(e) => setWriteContent(e.target.value)}
              maxLength={3000}
            />
          </div>
          <button
            style={{ ...s.submitBtn, opacity: writeSaving ? 0.6 : 1 }}
            onClick={handleSubmitPost}
            disabled={writeSaving || !writeTitle.trim() || !writeContent.trim()}
          >
            {writeSaving ? "저장 중..." : editingPost ? "수정 완료" : "등록하기"}
          </button>
        </div>
      </div>
    );
  }

  // ─── 글 목록 ───
  return (
    <div ref={containerRef} style={{ ...s.container, overflowY: "auto" }} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      {(pullY > 0 || refreshing) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: refreshing ? 40 : pullY, overflow: "hidden", transition: refreshing ? "height 0.2s" : "none", background: "#f7f8fa" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ ...s.spinner, width: 20, height: 20, borderWidth: 2 }} />
            <span style={{ fontSize: 12, color: "#9ca3af" }}>{refreshing ? "새로고침 중..." : pullY > 50 ? "놓으면 새로고침" : "당겨서 새로고침"}</span>
          </div>
        </div>
      )}
      <div style={s.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {onHome && (
            <button onClick={onHome} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center" }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
          )}
          <span style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>게시판</span>
        </div>
      </div>

      <div style={s.body}>
        {/* 탭 필터 */}
        <div style={s.tabRow}>
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              style={{ ...s.tabChip, background: tab === c.key ? "#E24B4A" : "#fff", color: tab === c.key ? "#fff" : "#6b7280", borderColor: tab === c.key ? "#E24B4A" : "#e5e7eb" }}
              onClick={() => handleTabChange(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* ─── 공지 탭 ─── */}
        {tab === "notice" ? (
          <>
            {isAdmin && !noticeFormMode && (
              <button style={s.writeBtn} onClick={() => openNoticeForm("create")}>
                + 공지 작성하기
              </button>
            )}

            {isAdmin && noticeFormMode && (
              <div style={s.noticeForm}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 10 }}>
                  {noticeFormMode === "edit" ? "공지 수정" : "새 공지 작성"}
                </div>
                <input
                  style={s.input}
                  placeholder="공지 제목"
                  value={noticeTitle}
                  onChange={(e) => setNoticeTitle(e.target.value)}
                  maxLength={100}
                />
                <textarea
                  style={{ ...s.input, resize: "none", height: 140, lineHeight: 1.7, marginTop: 10 }}
                  placeholder="공지 내용을 입력하세요"
                  value={noticeContent}
                  onChange={(e) => setNoticeContent(e.target.value)}
                  maxLength={2000}
                />
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <button style={s.noticeCancelBtn} onClick={closeNoticeForm}>취소</button>
                  <button
                    style={{ ...s.noticeSaveBtn, opacity: noticeSaving ? 0.6 : 1 }}
                    onClick={saveNotice}
                    disabled={noticeSaving || !noticeTitle.trim() || !noticeContent.trim()}
                  >
                    {noticeSaving ? "저장 중..." : "저장"}
                  </button>
                </div>
              </div>
            )}

            {notices.length === 0 ? (
              <div style={s.emptyBox}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>📢</div>
                <div style={{ fontSize: 14, color: "#6b7280", fontWeight: 600 }}>등록된 공지사항이 없어요</div>
              </div>
            ) : (
              notices.map((n) => (
                <div key={n.id} style={s.noticeItem}>
                  <button
                    style={s.noticeAccordionBtn}
                    onClick={() => setOpenNoticeId(openNoticeId === n.id ? null : n.id)}
                  >
                    <span style={{ flex: 1, textAlign: "left" }}>{n.title}</span>
                    <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 8 }}>{timeAgo(n.created_at)}</span>
                    <span style={{ fontSize: 12, color: "#9ca3af", flexShrink: 0, transition: "transform 0.2s", transform: openNoticeId === n.id ? "rotate(180deg)" : "rotate(0)", marginLeft: 8 }}>▼</span>
                  </button>
                  {openNoticeId === n.id && (
                    <div style={s.noticeBody}>
                      {n.content}
                      {isAdmin && (
                        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                          <button style={s.adminEditBtn} onClick={() => openNoticeForm("edit", n)}>수정</button>
                          <button style={s.adminDeleteBtn} onClick={() => deleteNotice(n.id)}>삭제</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </>
        ) : (
          <>
            {/* 글 작성 버튼 */}
            {user && (
              <button style={s.writeBtn} onClick={() => { setShowWrite(true); setEditingPost(null); setWriteTitle(""); setWriteContent(""); setWriteCategory("자유글"); }}>
                + 글 작성하기
              </button>
            )}

            {/* 전체글 탭: 공지 상단 고정 */}
            {tab === "all" && notices.length > 0 && notices.map((n) => (
              <div key={`notice-${n.id}`} style={s.noticeItem}>
                <button
                  style={s.noticeAccordionBtn}
                  onClick={() => setOpenNoticeId(openNoticeId === n.id ? null : n.id)}
                >
                  <span style={s.pinnedBadge}>공지</span>
                  <span style={{ flex: 1, textAlign: "left" }}>{n.title}</span>
                  <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 8 }}>{timeAgo(n.created_at)}</span>
                  <span style={{ fontSize: 12, color: "#9ca3af", flexShrink: 0, transition: "transform 0.2s", transform: openNoticeId === n.id ? "rotate(180deg)" : "rotate(0)", marginLeft: 8 }}>▼</span>
                </button>
                {openNoticeId === n.id && (
                  <div style={s.noticeBody}>{n.content}</div>
                )}
              </div>
            ))}

            {/* 목록 */}
            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
                <div style={s.spinner} />
              </div>
            ) : posts.length === 0 ? (
              <div style={s.emptyBox}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>📝</div>
                <div style={{ fontSize: 14, color: "#6b7280", fontWeight: 600 }}>게시글이 없어요</div>
              </div>
            ) : (
              posts.map((p) => (
                <div key={p.id} style={s.postCard} onClick={() => openDetail(p)}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span style={s.catBadge}>{p.category}</span>
                    <span style={{ fontSize: 11, color: "#9ca3af" }}>{timeAgo(p.created_at)}</span>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 4 }}>{p.title}</div>
                  <div style={{ fontSize: 12, color: "#9ca3af" }}>{p.nickname || p.email}</div>
                </div>
              ))
            )}

            {/* 페이지네이션 */}
            {totalPages > 1 && (
              <div style={s.pagination}>
                <button
                  style={{ ...s.pageBtn, opacity: page === 0 ? 0.4 : 1 }}
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                >
                  ◀
                </button>
                <span style={{ fontSize: 13, color: "#374151", fontWeight: 600 }}>
                  {page + 1} / {totalPages}
                </span>
                <button
                  style={{ ...s.pageBtn, opacity: page >= totalPages - 1 ? 0.4 : 1 }}
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                >
                  ▶
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const s = {
  container:   { minHeight: "100vh", background: "#f7f8fa", display: "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto", fontFamily: "'Noto Sans KR', sans-serif" },
  header:      { padding: "calc(env(safe-area-inset-top) + 18px) 20px 16px", borderBottom: "1px solid #edf0f3", background: "#fff", position: "sticky", top: 0, zIndex: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.04)" },
  logoRow:     { position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 },
  logoCenterWrap: { position: "absolute", left: "50%", transform: "translateX(-50%)", display: "flex", alignItems: "baseline", gap: 4 },
  homeBtn:     { background: "none", border: "none", fontSize: 20, cursor: "pointer", padding: "4px", lineHeight: 1 },
  logoText:    { fontSize: 20, fontWeight: 800, color: "#111827", letterSpacing: "-0.5px" },
  logoSub:     { fontSize: 12, color: "#9ca3af", fontWeight: 400 },
  headerTitle: { fontSize: 16, fontWeight: 700, color: "#111827" },
  backBtn:     { display: "flex", alignItems: "center", gap: 2, background: "none", border: "none", fontSize: 14, fontWeight: 600, color: "#E24B4A", cursor: "pointer", padding: "4px 0" },
  headerActionBtn: { padding: "5px 10px", background: "#f5f6fa", color: "#374151", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  body:        { flex: 1, padding: "16px 20px 32px" },
  sectionTitle:{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 8 },
  tabRow:      { display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" },
  tabChip:     { padding: "7px 14px", border: "1px solid #e5e7eb", borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  catChip:     { padding: "8px 18px", border: "1px solid #e5e7eb", borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  catBadge:    { padding: "2px 8px", background: "#f5f6fa", border: "1px solid #edf0f3", borderRadius: 999, fontSize: 11, fontWeight: 600, color: "#6b7280" },
  writeBtn:    { width: "100%", padding: "12px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", marginBottom: 14 },
  postCard:    { background: "#fff", borderRadius: 12, border: "1px solid #edf0f3", padding: "14px 16px", marginBottom: 8, cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,0.03)" },
  emptyBox:    { textAlign: "center", padding: "40px 20px", background: "#fff", borderRadius: 14, border: "1px solid #edf0f3" },
  spinner:     { width: 28, height: 28, border: "3px solid #edf0f3", borderTop: "3px solid #E24B4A", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  pagination:  { display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginTop: 16 },
  pageBtn:     { padding: "6px 14px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 13, cursor: "pointer", fontFamily: "inherit" },
  input:       { width: "100%", padding: "12px 14px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, fontSize: 14, color: "#111827", outline: "none", boxSizing: "border-box", fontFamily: "inherit" },
  submitBtn:   { width: "100%", padding: "14px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  submitSmallBtn: { padding: "10px 16px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 },
  detailCard:  { background: "#fff", borderRadius: 14, border: "1px solid #edf0f3", padding: "18px 16px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" },
  commentItem: { background: "#fff", borderRadius: 10, border: "1px solid #edf0f3", padding: "12px 14px", marginBottom: 8 },
  commentDeleteBtn: { background: "none", border: "none", fontSize: 11, color: "#E24B4A", cursor: "pointer", padding: 0, fontFamily: "inherit" },
  smallEditBtn:   { padding: "6px 14px", background: "#f5f6fa", color: "#374151", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  smallDeleteBtn: { padding: "6px 14px", background: "#fff5f5", color: "#E24B4A", border: "1px solid #fecaca", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  pinnedBadge:    { padding: "2px 8px", background: "#E24B4A", color: "#fff", borderRadius: 999, fontSize: 11, fontWeight: 700, flexShrink: 0, marginRight: 4 },
  noticeItem:     { background: "#fff", borderRadius: 12, border: "1px solid #edf0f3", marginBottom: 8, overflow: "hidden" },
  noticeAccordionBtn: { width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "14px 16px", background: "none", border: "none", fontSize: 14, fontWeight: 600, color: "#111827", cursor: "pointer", fontFamily: "inherit" },
  noticeBody:     { padding: "0 16px 14px", fontSize: 13, color: "#6b7280", lineHeight: 1.7, whiteSpace: "pre-wrap" },
  noticeForm:     { background: "#fff", borderRadius: 14, border: "1px solid #edf0f3", padding: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", marginBottom: 14 },
  noticeCancelBtn:{ flex: 1, padding: "11px", background: "#f7f8fa", color: "#6b7280", border: "1px solid #edf0f3", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  noticeSaveBtn:  { flex: 2, padding: "11px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  adminEditBtn:   { padding: "6px 14px", background: "#f5f6fa", color: "#374151", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  adminDeleteBtn: { padding: "6px 14px", background: "#fff5f5", color: "#E24B4A", border: "1px solid #fecaca", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
};
